import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import API_URL from '../config';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PdfDocument from './PdfDocument';
import {
    DetailContainer,
    Header,
    ProjectInfo,
    Section,
    Checklist,
    ChecklistItem,
    ImageGrid,
    Image,
    PlanImage,
    Button,
    ButtonContainer,
} from './Detail.styles';
import { Project, Vloerverwarming, Klimaatwand, Klimaatplafond, ProjectSection } from '../types';
import logo from '../assets/logo.png';
import LoadingOverlay from './LoadingOverlay';

const Detail: React.FC = () => {
    const [project, setProject] = useState<Project | null>(null);
    const [projectForPdf, setProjectForPdf] = useState<Project | null>(null);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const response = await api.get(`/api/projects/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                setProject(response.data);
            } catch (error) {
                console.error('Error fetching project', error);
            }
        };
        fetchProject();
    }, [id]);

    useEffect(() => {
        const convertImagesToDataUris = async () => {
            if (!project) return;

            console.log('[PDF] Starting image conversion for project:', project.title, '— sections:', project.sections.length);

            const MAX_IMAGE_DIMENSION = 800;

            const resizeImage = (dataUrl: string): Promise<string> =>
                new Promise((resolve) => {
                    const img = new window.Image();
                    img.onload = () => {
                        const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.width, img.height));
                        const canvas = document.createElement('canvas');
                        canvas.width = Math.round(img.width * scale);
                        canvas.height = Math.round(img.height * scale);
                        const ctx = canvas.getContext('2d')!;
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        resolve(canvas.toDataURL('image/png'));
                    };
                    img.onerror = () => resolve(dataUrl);
                    img.src = dataUrl;
                });

            const toDataURL = async (url: string): Promise<string | null> => {
                try {
                    console.log('[PDF] Fetching image:', url);
                    const response = await api.get(url, {
                        responseType: 'blob',
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    });
                    console.log('[PDF] Image fetched OK:', url, '— size:', response.data.size, 'type:', response.data.type);
                    const rawDataUrl = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(response.data);
                    });
                    const resized = await resizeImage(rawDataUrl);
                    console.log('[PDF] Image resized to data URI:', url, '— length:', resized.length);
                    return resized;
                } catch (err) {
                    console.error('[PDF] Failed to fetch image:', url, err);
                    return null;
                }
            };

            const sectionsWithDataUris = await Promise.all(project.sections.map(async (section) => {
                console.log('[PDF] Processing section:', section.sectionName, '— images:', section.images.length, 'plan:', !!section.plan);
                let planWithDataUri = section.plan;
                if (section.plan) {
                    if (typeof section.plan === 'string') {
                        const imageUrl = `${API_URL}/${section.plan}`;
                        const dataUrl = await toDataURL(imageUrl);
                        if (dataUrl) planWithDataUri = { url: dataUrl, key: section.plan };
                        else console.warn('[PDF] Plan image failed, skipping:', imageUrl);
                    } else if ('key' in section.plan && typeof section.plan.key === 'string') {
                        const proxyUrl = `/api/projects/image-proxy?key=${section.plan.key}`;
                        const dataUrl = await toDataURL(proxyUrl);
                        if (dataUrl) planWithDataUri = { ...section.plan, url: dataUrl };
                        else console.warn('[PDF] Plan image (proxy) failed, skipping:', proxyUrl);
                    }
                }

                const imagesWithDataUris = await Promise.all(section.images.map(async (image, idx) => {
                    if (typeof image === 'string') {
                        const imageUrl = `${API_URL}/${image}`;
                        const dataUrl = await toDataURL(imageUrl);
                        if (!dataUrl) console.warn('[PDF] Section image failed, skipping index', idx, ':', imageUrl);
                        return dataUrl ? { url: dataUrl, key: image } : image;
                    }
                    if ('key' in image && typeof image.key === 'string') {
                        const proxyUrl = `/api/projects/image-proxy?key=${image.key}`;
                        const dataUrl = await toDataURL(proxyUrl);
                        if (!dataUrl) console.warn('[PDF] Section image (proxy) failed, skipping index', idx, ':', proxyUrl);
                        return dataUrl ? { ...image, url: dataUrl } : image;
                    }
                    return image;
                }));

                return { ...section, plan: planWithDataUri, images: imagesWithDataUris };
            }));

            console.log('[PDF] Image conversion complete, setting projectForPdf');
            setProjectForPdf({ ...project, sections: sectionsWithDataUris });
        };

        convertImagesToDataUris().catch((error) => {
            console.error('[PDF] Unexpected error during image conversion:', error);
            setProjectForPdf(project);
        });
    }, [project]);

    if (!project) {
        return <div>Loading...</div>;
    }

    return (
        <DetailContainer>
            <Header>
                <div>
                    <img src={logo} alt="Apply Heating Logo" />
                </div>
                <h2>Projectrapport</h2>
            </Header>

            <ProjectInfo>
                <p><strong>Klant:</strong> {project.klant}</p>
                <p><strong>Adres:</strong> {project.adres}</p>
                <p><strong>Info:</strong> {project.info}</p>
            </ProjectInfo>

            <Section>
                <h4>VLOERVERWARMING:</h4>
                <div className="grid">
                    {Object.values(Vloerverwarming).map((value) => (
                        <div key={value} className="grid-item">
                            <input type="checkbox" checked={project.vloerverwarming.includes(value)} readOnly />
                            <span>{value}</span>
                        </div>
                    ))}
                </div>
            </Section>

            <Section>
                <h4>WANDKLIMAAT</h4>
                <div className="grid">
                    {Object.values(Klimaatwand).map((value) => (
                        <div key={value} className="grid-item">
                            <input type="radio" checked={project.klimaatwand === value} readOnly />
                            <span>{value}</span>
                        </div>
                    ))}
                </div>
            </Section>

            <Section>
                <h4>PLAFONDKLIMAAT</h4>
                <div className="grid">
                    {Object.values(Klimaatplafond).map((value) => (
                        <div key={value} className="grid-item">
                            <input type="radio" checked={project.klimaatplafond === value} readOnly />
                            <span>{value}</span>
                        </div>
                    ))}
                </div>
            </Section>

            <Checklist>
                <h4>CHECKLIST</h4>
                <ChecklistItem>
                    <span>Volgens legplan:</span>
                    <span>{project.volgensLegplan ? 'ja' : 'nee'}</span>
                </ChecklistItem>
                <ChecklistItem>
                    <span>Kringnummers aangegeven op verdelers?</span>
                    <span>{project.kringnummersAangegevenOpVerdelers ? 'ja' : 'nee'}</span>
                </ChecklistItem>
                <ChecklistItem>
                    <span>Installatie dicht:</span>
                    <span>{project.installatieDicht ? 'ja' : 'nee'}</span>
                </ChecklistItem>
            </Checklist>

            {project.sections.map((section: ProjectSection, index: number) => (
                <div key={section.id}>
                    <Section>
                        <h3>{section.sectionName}</h3>
                        <p>- Afgeperst met: {section.afgeperstMet}</p>
                        <p>- Hoeveelheid druk (bar): {section.hoeveelheidDruk}</p>
                        <p>- Buitentemperatuur (°C): {section.buitenTemperatuur}</p>
                    </Section>
                    {section.plan && (() => {
                        const planUrl = typeof section.plan === 'string' ? `${API_URL}/${section.plan}` : (section.plan as any).url;
                        return planUrl && (
                            <div>
                                <h4>Plan</h4>
                                <a href={planUrl} target="_blank" rel="noopener noreferrer">
                                    <PlanImage src={planUrl} alt="Plan" />
                                </a>
                            </div>
                        );
                    })()}
                    {section.images && section.images.length > 0 && (
                        <div>
                            <h4>Images</h4>
                            <ImageGrid>
                                {section.images.map((image, idx) => {
                                    const imageUrl = typeof image === 'string' ? `${API_URL}/${image}` : (image as any).url;
                                    return imageUrl && (
                                        <a href={imageUrl} target="_blank" rel="noopener noreferrer" key={idx}>
                                            <Image src={imageUrl} alt={`Image ${idx}`} />
                                        </a>
                                    );
                                })}
                            </ImageGrid>
                        </div>
                    )}
                </div>
            ))}
            <ButtonContainer>
                {projectForPdf ? (
                    <PDFDownloadLink document={<PdfDocument data={projectForPdf} />} fileName={`${project.title}.pdf`}>
                        {({ loading, error }) => {
                            if (error) console.error('[PDF] PDFDownloadLink render error:', error);
                            return loading ? <LoadingOverlay /> : error ? <Button disabled>PDF Error</Button> : <Button>Download PDF</Button>;
                        }}
                    </PDFDownloadLink>
                ) : (
                    <Button disabled>Loading PDF...</Button>
                )}
                <Button onClick={() => navigate(`/edit/${id}`)}>Edit Project</Button>
            </ButtonContainer>
        </DetailContainer>
    );
};

export default Detail;
